<template>
  <BasicBlock
    :title="$t('shipping_method.block.title')"
    v-if="formResource.allow_shipping_methods"
    v-show="showShippingMethods"
  >
    <ValidationProvider
      ref="shippingMethod"
      :rules="rulesObject"
      :skipIfEmpty="false"
      slim
      v-slot="{ validate, errors }"
    >
      <input
        type="hidden"
        name="shipping_method_hidden"
        @change="validate"
        v-model="value"
      />

      <SimpleErrorLine
        v-if="errors.length > 0"
        :message="$t('shipping_method.validations.required')"
      ></SimpleErrorLine>
    </ValidationProvider>

    <div
      :class="
        singleColumn
          ? 'f-grid f-grid-cols-1 sm:f-gap-3 '
          : 'f-grid f-grid-cols-1 sm:f-grid-cols-2 sm:f-gap-3'
      "
    >
      <ShippingMethod
        :class="[
          'fapi-form-shipping-method',
          'fapi-form-shipping-method-' + index,
        ]"
        v-for="(shippingMethod, index) in shippingMethods"
        v-bind:key="index + '-shipping-method'"
        :formResource="formResource"
        :templateSettings="templateSettings"
        :shippingMethod="shippingMethod"
        :packateryApiKey="packateryApiKey"
        :currentlySelectedRadio="value"
        :currency="currency"
        :country="country"
        :order="order"
        :fullInvoiceOrIsAddress="fullInvoiceOrIsAddress"
        @shippingMethodChanged="shippingMethodChanged"
        @shippingMethodVisibilityChanged="shippingMethodVisibilityChanged"
        @isEligibleForFreeShipping="$emit('isEligibleForFreeShipping', $event)"
      ></ShippingMethod>
    </div>
  </BasicBlock>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import FormResource from "@/module/Form/Resources/FormResource";
import ShippingMethod from "@/components/Form/ShippingMethods/ShippingMethod.vue";
import FormShippingItem from "@/module/FormShippingItem";
import TemplateSettings from "@/module/Form/Resources/TemplateSettings";
import { isStringOrNull } from "@/module/ComponentPropsValidations/validations";
import { ValidationProvider } from "vee-validate";
import SimpleErrorLine from "@/components/Form/SimpleErrorLine.vue";
import OrderPrices from "@/module/Order/OrderPrices";

interface ComponentData {
  value: ShippingMethodValue | null;
  shippingMethodsVisibility: { [key: number]: boolean };
  atLeastOnVisible: boolean;
}

interface ShippingMethodValue {
  shipping_method_id: number;
  shipping_metadata: any | undefined;
}

export default Vue.extend({
  name: "ShippingMethods",

  components: {
    SimpleErrorLine,
    ShippingMethod,
    BasicBlock,
    ValidationProvider,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    templateSettings: {
      type: Object as PropType<TemplateSettings>,
      required: true,
    },
    isAddressShow: {
      type: Boolean,
      required: true,
    },
    showShippingMethods: {
      type: Boolean,
      required: true,
    },
    shippingMethods: {
      type: Array as PropType<Array<FormShippingItem>>,
      required: true,
    },
    fullInvoice: {
      type: Boolean,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    addressCountry: {
      type: null as unknown as PropType<string | null>,
      required: true,
      validator: isStringOrNull,
    },
    shippingCountry: {
      type: null as unknown as PropType<string | null>,
      required: true,
      validator: isStringOrNull,
    },
    withShippingAddress: {
      type: Boolean,
      required: true,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      value: null,
      shippingMethodsVisibility: {},
      atLeastOnVisible: false,
    };
  },

  computed: {
    rulesObject(): object {
      return this.showShippingMethods ? { required: true } : {};
    },

    country(): string | null {
      return this.withShippingAddress
        ? this.shippingCountry
        : this.addressCountry;
    },

    singleColumn(): boolean {
      return (
        this.templateSettings.container_settings.shipping.layout.column ===
        "single"
      );
    },

    fullInvoiceOrIsAddress(): boolean {
      return this.isAddressShow || this.fullInvoice;
    },

    packateryApiKey(): string | null {
      if (this.formResource.packatery === undefined) {
        return null;
      }

      return this.formResource.packatery.api_key;
    },
  },

  methods: {
    shippingMethodChanged(value: ShippingMethodValue | null): void {
      this.value = value;
      this.$emit("shippingMethodChanged", value);
    },

    shippingMethodVisibilityChanged(value: {
      shippingMethodId: number;
      visible: boolean;
    }): void {
      this.shippingMethodsVisibility[value.shippingMethodId] = value.visible;
      this.atLeastOnVisible = Boolean(
        Object.values(this.shippingMethodsVisibility).find((item) => {
          return item;
        }),
      );
    },
  },

  watch: {
    showShippingMethods(): void {
      if (this.showShippingMethods) {
        this.shippingMethodChanged(this.value);
      } else {
        this.$emit("shippingMethodChanged", {
          shipping_method_id: null,
          shipping_metadata: null,
        });
      }
    },
  },

  mounted(): void {
    this.$nextTick(() => {
      const mountedEvent = new CustomEvent("fapiFormShippingMounted");
      document.dispatchEvent(mountedEvent);
    });
  },
});
</script>

<style lang="scss"></style>
